import { useState } from "react";
import moment from "moment";
import { patchOrderBaristaByID, OrderResponse } from "../../../../../api/order.api";
import CancelOrder from "../../../modal_page/cancelOrder";
import ButtonDone from "./buttonDone";
import { ikThumb } from "../../../../../utils/image";

const TYPE_META: Record<string, { emoji: string; label: string; tone: string }> = {
  hot:    { emoji: "🔥", label: "Hot",    tone: "bg-orange-100 text-orange-900" },
  cold:   { emoji: "🧊", label: "Cold",   tone: "bg-sky-100 text-sky-900" },
  frappe: { emoji: "🥤", label: "Frappe", tone: "bg-emerald-100 text-emerald-900" },
};

type Props = {
  data?: OrderResponse[];
  editorMode: boolean;
  status?: "incoming" | "inprogress" | "ready";
  reset: () => void;
  select?: (id: number) => void;
  selected: number[];
};

function IncomingOrder({
  select, selected, status, editorMode, reset, data = [],
}: Props) {
  const [rejectID, setRejectID] = useState<number | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [imgPreview, setImgPreview] = useState("");

  if (data.length === 0) return null;

  const closeModal = () => { setRejectID(null); reset(); };

  const startBrewing = async (orderId: number) => {
    try {
      await patchOrderBaristaByID(orderId, { reject_note: "", status: "in-progress" });
      reset();
    } catch (err) {
      console.error(err);
    }
  };

  const markPicked = async (orderId: number) => {
    try {
      await patchOrderBaristaByID(orderId, { reject_note: "", status: "picked" });
      reset();
    } catch (err) {
      console.error(err);
    }
  };

  const sectionMeta =
    status === "ready"      ? { label: "Ready · waiting for pickup", emoji: "✓", color: "#059669", bg: "#D1FAE5" }
    : status === "inprogress" ? { label: "Brewing",                    emoji: "🔥", color: "#EA580C", bg: "#FED7AA" }
    :                           { label: "New orders",                 emoji: "⏳", color: "#2563EB", bg: "#DBEAFE" };

  return (
    <>
      <CancelOrder reject={rejectID} onClose={closeModal} />

      {/* Image preview overlay */}
      {openPreview && (
        <div
          onClick={() => setOpenPreview(false)}
          className="fixed inset-0 z-[60] bg-ink-1000/70 flex items-center justify-center p-4"
        >
          <img src={imgPreview} alt="tumbler" className="max-w-full max-h-[80vh] object-contain rounded-2xl" />
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-black text-[20px] tracking-tight flex items-center gap-2" style={{ color: "#1B0F08" }}>
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-sm"
              style={{ background: sectionMeta.bg, color: sectionMeta.color }}
              aria-hidden
            >
              {sectionMeta.emoji}
            </span>
            {sectionMeta.label}
          </h2>
          <span
            className="text-xs font-extrabold tabular-nums px-2.5 h-[22px] inline-flex items-center rounded-full"
            style={{ background: sectionMeta.bg, color: sectionMeta.color }}
          >
            {data.length}
          </span>
        </div>

        <div className="space-y-2.5">
          {data.map((order) => {
            const typeMeta = TYPE_META[order.type] ?? { emoji: "", label: order.type, tone: "bg-ink-100 text-ink-700" };
            const isPicked = selected.includes(order.id);
            const isDessert = order.menu?.category?.categoryFor === "dessert";
            const menuImg = order.menu?.image;

            return (
              <article
                key={order.id}
                className={`card-v2 overflow-hidden flex transition-colors ${
                  isPicked && editorMode ? "border-ink-1000 bg-ink-50" : ""
                }`}
              >
                {/* Bulk select checkbox */}
                {editorMode && (
                  <button
                    type="button"
                    onClick={() => select?.(order.id)}
                    aria-label="select"
                    className="px-4 flex items-center"
                  >
                    <span
                      className={`w-[22px] h-[22px] rounded-md border-2 flex items-center justify-center transition ${
                        isPicked ? "bg-accent border-accent text-accent-on" : "border-ink-300 bg-white"
                      }`}
                    >
                      {isPicked && (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                      )}
                    </span>
                  </button>
                )}

                {/* Image — tumbler for drinks, menu image for desserts.
                    Bumped from 88px → 128px so it reads from across the bar. */}
                {isDessert ? (
                  <div className="flex-shrink-0 p-3">
                    <div className="w-[128px] h-[128px] rounded-xl bg-ink-100 flex items-center justify-center overflow-hidden">
                      {menuImg ? (
                        <img
                          src={ikThumb(menuImg, 280, 280)}
                          alt={order.menu.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="font-display font-black text-3xl text-ink-300">
                          {order.menu.name.slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { if (!editorMode) { setImgPreview(order.tumblerImage); setOpenPreview(true); } }}
                    className="flex-shrink-0 p-3"
                  >
                    <img
                      src={order.tumblerImage}
                      alt="tumbler"
                      className="w-[128px] h-[128px] object-cover rounded-xl"
                    />
                  </button>
                )}

                {/* Body */}
                <div className="flex-1 min-w-0 py-3 pr-3">
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <span className="text-xs text-ink-400 font-medium">
                      {moment(order.orderDate).format("HH:mm")}
                    </span>
                  </div>

                  {/* Menu name + temperature/dessert badge inline. The badge is
                      sized up (h-[28px], text-sm) so it's legible at arm's
                      length while baristas plate up. */}
                  <div className="flex items-center gap-2 flex-wrap leading-tight">
                    <h3 className="font-display font-bold text-base text-ink-1000 truncate min-w-0">
                      {order.menu.name}
                    </h3>
                    {isDessert ? (
                      <span className="px-3 h-[28px] inline-flex items-center gap-1 rounded-full text-sm font-bold bg-pink-100 text-pink-900 shrink-0">
                        🍰 Dessert
                      </span>
                    ) : (
                      <span className={`px-3 h-[28px] inline-flex items-center gap-1 rounded-full text-sm font-bold shrink-0 ${typeMeta.tone}`}>
                        {typeMeta.emoji} {typeMeta.label}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-ink-500 mt-1 truncate">
                    {order.user.name}
                    {!isDessert && order.menu.category.categoryFor === "cafe" && ` · ${order.sweetness}% sweet`}
                  </p>

                  {order.remark && order.remark !== "-" && (
                    <p className="text-xs text-ink-700 italic mt-1.5 line-clamp-2 px-2.5 py-1.5 rounded-lg bg-ink-50">
                      "{order.remark}"
                    </p>
                  )}

                  {/* Actions */}
                  {!editorMode && (
                    <div className="mt-3 flex gap-2 justify-end">
                      {status === "ready" ? (
                        <button
                          type="button"
                          onClick={() => markPicked(order.id)}
                          className="btn-v2-primary btn-v2-sm"
                        >
                          Picked up
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                          </svg>
                        </button>
                      ) : status === "inprogress" ? (
                        <ButtonDone reset={reset} order={order} />
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setRejectID(order.id)}
                            className="btn-v2-secondary btn-v2-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => startBrewing(order.id)}
                            className="inline-flex items-center justify-center gap-2 h-[44px] px-6 text-sm font-bold rounded-full bg-amber-500 text-white hover:bg-amber-600 active:scale-[0.97] shadow-soft transition"
                          >
                            Start brewing
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/>
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}

export default IncomingOrder;
